
// src/hooks/useLabRequests.ts

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  LAB_STORAGE_KEYS,
  loadLabStorage,
  removeLabStorage,
  saveLabStorage,
} from "../utils/labStorage";

import {
  LAB_TEST_REQUESTS,
} from "../data/labMockData";

import type {
  OngoingTest,
  TestRequest,
} from "../types/lab-context.types";

interface UseLabRequestsOptions {
  /**
   * Called when an accepted request should be added
   * to the ongoing-tests collection.
   */
  onRequestAccepted?: (
    test: OngoingTest,
  ) => void;


   // Name of the authenticated laboratory professional.
   // Replace this with the current user when authentication
   // data is connected.

  currentLabScientist?: string;
}

interface UseLabRequestsReturn {
  testRequests: TestRequest[];

  acceptRequest: (
    request: TestRequest,
  ) => void;

  rejectRequest: (
    requestId: string,
  ) => void;

  bulkAcceptRequests: (
    requestIds: string[],
  ) => void;

  bulkRejectRequests: (
    requestIds: string[],
  ) => void;

  clearTestRequests: () => void;
  resetTestRequests: () => void;
}

/**
 * Manages laboratory test-request state and actions.
 *
 * This hook currently persists data to localStorage.
 * Replace the local state operations with API calls once
 * the laboratory backend is connected.
 */
export const useLabRequests = ({
  onRequestAccepted,
  currentLabScientist = "Current User",
}: UseLabRequestsOptions = {}): UseLabRequestsReturn => {
  const [testRequests, setTestRequests] =
    useState<TestRequest[]>(() =>
      loadLabStorage(
        LAB_STORAGE_KEYS.TEST_REQUESTS,
        LAB_TEST_REQUESTS,
      ),
    );

  useEffect(() => {
    saveLabStorage(
      LAB_STORAGE_KEYS.TEST_REQUESTS,
      testRequests,
    );
  }, [testRequests]);

  /**
   * Converts an accepted request into an ongoing test.
   */
  const createOngoingTest = useCallback(
    (
      request: TestRequest,
    ): OngoingTest => {
      return {
        id: request.id,

        patientId: request.patientId,
        patientName: request.patientName,
        age: request.age,
        gender: request.gender,

        testType: request.testType,
        requestedBy: request.requestedBy,
        hospital: request.hospital,

        priority: request.priority,

        status: "sample_collection",
        progress: 10,

        startTime:
          new Date().toISOString(),

        estimatedCompletion:
          request.estimatedTime,

        labScientist:
          currentLabScientist,
      };
    },
    [currentLabScientist],
  );

  const acceptRequest = useCallback(
    (
      request: TestRequest,
    ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [
      createOngoingTest,
      onRequestAccepted,
    ],
  );

  const rejectRequest = useCallback(
    (
      requestId: string,
    ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [],
  );

  const bulkAcceptRequests =
    useCallback(
      (
        requestIds: string[],
      ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      [
        createOngoingTest,
        onRequestAccepted,
        testRequests,
      ],
    );

  const bulkRejectRequests =
    useCallback(
      (
        requestIds: string[],
      ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      [],
    );

  const clearTestRequests =
    useCallback((): void => {
      removeLabStorage(
        LAB_STORAGE_KEYS.TEST_REQUESTS,
      );

      setTestRequests([]);
    }, []);

  const resetTestRequests =
    useCallback((): void => {
      setTestRequests(
        LAB_TEST_REQUESTS,
      );
    }, []);

  return {
    testRequests,

    acceptRequest,
    rejectRequest,

    bulkAcceptRequests,
    bulkRejectRequests,

    clearTestRequests,
    resetTestRequests,
  };
};

